import React, { useState } from 'react';
import { X, Download, AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Office Document Viewer Component
 * Displays Word, Excel, and PowerPoint documents using Microsoft Office Online Viewer
 * Falls back to Google Docs Viewer if Microsoft service fails
 */
const OfficeViewer = ({ documentUrl, fileName, documentType, onClose }) => {
  const [viewerService, setViewerService] = useState('microsoft'); // 'microsoft' or 'google'
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Build viewer URL based on selected service
  const getViewerUrl = () => {
    if (!documentUrl) return '';

    // Encode the document URL
    const encodedUrl = encodeURIComponent(documentUrl);

    if (viewerService === 'microsoft') {
      // Microsoft Office Online Viewer
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    } else {
      // Google Docs Viewer (fallback)
      return `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const switchToGoogleViewer = () => {
    setViewerService('google');
    setIsLoading(true);
    setHasError(false);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName || 'document';
    link.click();
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setHasError(false);
    // Force iframe reload by changing key
    setViewerService(prev => prev === 'microsoft' ? 'microsoft' : 'google');
  };

  const viewerUrl = getViewerUrl();

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate" title={fileName}>
              {fileName || 'Document Office'}
            </h2>
            <p className="text-sm text-gray-500">
              {documentType || 'Document'} • Visualiseur {viewerService === 'microsoft' ? 'Microsoft Office' : 'Google Docs'}
            </p>
          </div>

          <div className="flex items-center gap-2 ml-4">
            {/* Download button */}
            <button
              onClick={handleDownload}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              title="Télécharger le document"
            >
              <Download className="h-5 w-5" />
            </button>

            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="h-5 w-5" />
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              title="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Viewer Content */}
        <div className="flex-1 relative overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Chargement du document...</p>
              </div>
            </div>
          )}

          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 p-8">
              <div className="max-w-md text-center">
                <AlertCircle className="h-16 w-16 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Impossible de charger le document
                </h3>
                <p className="text-gray-600 mb-6">
                  Le visualiseur {viewerService === 'microsoft' ? 'Microsoft Office' : 'Google Docs'} n'a pas pu charger ce document.
                  Cela peut être dû à des restrictions d'accès ou à un format non supporté.
                </p>

                <div className="flex flex-col gap-3">
                  {viewerService === 'microsoft' && (
                    <button
                      onClick={switchToGoogleViewer}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Essayer avec Google Docs Viewer
                    </button>
                  )}

                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger le document
                  </button>

                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}

          {viewerUrl && (
            <iframe
              key={`${viewerService}-${documentUrl}`}
              src={viewerUrl}
              className="w-full h-full border-0"
              title={fileName || 'Document Office'}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          )}
        </div>

        {/* Footer with info */}
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            {viewerService === 'microsoft'
              ? 'Visualisation fournie par Microsoft Office Online'
              : 'Visualisation fournie par Google Docs Viewer'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OfficeViewer;
